import React, { useState, useEffect } from 'react';
import { appStoreService, appUserReviewService } from '../../lib/services/appstore/index.js';
import { useTranslation } from '../../lib/i18n';
import LanguageSwitcher from '../Common/LanguageSwitcher';
import './AppDetails.css';

const AppDetails = ({ appId, currentTenant, currentUser, onBack }) => {
  const { t } = useTranslation();
  const [app, setApp] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Review form state
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    review_text: '',
    helpful_count: 0,
    is_verified_user: false
  });

  useEffect(() => {
    fetchAppDetails();
  }, [appId, currentTenant]);

  const fetchAppDetails = async () => {
    try {
      setLoading(true);
      const [appResponse, reviewsResponse, statsResponse] = await Promise.all([
        appStoreService.getAppById(appId, currentTenant),
        appUserReviewService.getAppReviews(appId, currentTenant, { limit: 10 }),
        appUserReviewService.getAppReviewStats(appId, currentTenant)
      ]);

      setApp(appResponse.data);
      setReviews(reviewsResponse.data);
      setReviewStats(statsResponse);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewSubmit = async () => {
    if (!currentUser) {
      alert('Please log in to submit a review');
      return;
    }

    if (!reviewForm.review_text.trim()) {
      alert('Please enter a review');
      return;
    }

    try {
      await appUserReviewService.submitReview(
        currentUser.id,
        appId,
        reviewForm,
        currentTenant
      );
      
      setShowReviewForm(false);
      setReviewForm({
        rating: 5,
        review_text: '',
        helpful_count: 0,
        is_verified_user: false
      });
      
      // Refresh reviews
      fetchAppDetails();
    } catch (err) {
      alert(`Failed to submit review: ${err.message}`);
    }
  };

  const handleReviewHelpful = async (reviewId) => {
    if (!currentUser) {
      alert('Please log in to mark reviews as helpful');
      return;
    }

    try {
      await appUserReviewService.markReviewHelpful(reviewId, currentUser.id, currentTenant);
      // Refresh reviews
      fetchAppDetails();
    } catch (err) {
      alert(`Failed to mark review as helpful: ${err.message}`);
    }
  };

  const handleAppInstall = async () => {
    // This would integrate with the installation service
    console.log('Installing app:', appId);
    // TODO: Implement actual installation logic
  };

  const renderRatingStars = (rating) => {
    return '⭐'.repeat(Math.floor(rating)) + '☆'.repeat(5 - Math.floor(rating));
  };

  const renderReviewForm = () => (
    <div className="review-form-modal">
      <div className="review-form-content">
        <h3>Write a Review</h3>
        <div className="rating-input">
          <label>Rating:</label>
          <div className="star-rating">
            {[1, 2, 3, 4, 5].map(star => (
              <button
                key={star}
                className={`star ${star <= reviewForm.rating ? 'filled' : ''}`}
                onClick={() => setReviewForm(prev => ({ ...prev, rating: star }))}
              >
                ⭐
              </button>
            ))}
          </div>
        </div>
        <div className="review-text-input">
          <label>Review:</label>
          <textarea
            value={reviewForm.review_text}
            onChange={(e) => setReviewForm(prev => ({ ...prev, review_text: e.target.value }))}
            placeholder="Share your experience with this app..."
            rows={4}
          />
        </div>
        <div className="review-form-actions">
          <button onClick={handleReviewSubmit} className="submit-review-btn">
            Submit Review
          </button>
          <button onClick={() => setShowReviewForm(false)} className="cancel-btn">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="app-details">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading app details...</p>
        </div>
      </div>
    );
  }

  if (error || !app) {
    return (
      <div className="app-details">
        <div className="error-container">
          <h3>Error</h3>
          <p>{error || 'App not found'}</p>
          <button onClick={onBack}>Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-details">
      {/* Header */}
      <div className="app-details-header">
        <button onClick={onBack} className="back-btn">
          ← {t('common.back')} {t('common.to')} {t('enhancedAppStore.title')}
        </button>
        <LanguageSwitcher />
      </div>

      {/* App Overview */}
      <div className="app-overview">
        <div className="app-header">
          <div className="app-icon-large">
            {app.app_icon_url ? (
              <img src={app.app_icon_url} alt={app.app_name} />
            ) : (
              <div className="app-icon-placeholder-large">
                {app.app_name.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className="app-header-info">
            <h1>{app.app_name}</h1>
            <p className="app-developer">by {app.app_developer?.developer_name || 'Unknown'}</p>
            <div className="app-meta-large">
              <span className="app-category">{app.app_category}</span>
              <span className="app-rating-large">
                {renderRatingStars(app.rating || 0)} ({app.rating || 0})
              </span>
              {app.is_free && <span className="app-free">Free</span>}
              {app.has_trial && <span className="app-trial">Trial Available</span>}
            </div>
            <div className="app-stats-large">
              <span>{app.total_downloads || 0} downloads</span>
              <span>{app.total_reviews || 0} reviews</span>
              <span>Version {app.app_version || '1.0'}</span>
            </div>
          </div>
          <div className="app-actions-large">
            <button onClick={handleAppInstall} className="install-btn-large">
              Install App
            </button>
            {currentUser && (
              <button 
                onClick={() => setShowReviewForm(true)} 
                className="review-btn"
              >
                Write Review
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="app-details-tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab ${activeTab === 'reviews' ? 'active' : ''}`}
          onClick={() => setActiveTab('reviews')}
        >
          Reviews ({reviewStats?.totalReviews || 0})
        </button>
        <button 
          className={`tab ${activeTab === 'details' ? 'active' : ''}`}
          onClick={() => setActiveTab('details')}
        >
          Details
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="overview-content">
            <div className="app-description-large">
              <h3>Description</h3>
              <p>{app.app_description}</p>
            </div>
            
            {app.features && app.features.length > 0 && (
              <div className="app-features">
                <h3>Features</h3>
                <ul>
                  {app.features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
              </div>
            )}

            {app.tags && app.tags.length > 0 && (
              <div className="app-tags">
                <h3>Tags</h3>
                <div className="tags-container">
                  {app.tags.map((tag, index) => (
                    <span key={index} className="tag">{tag}</span>
                  ))}
                </div>
              </div>
            )}

            {reviewStats && (
              <div className="review-summary">
                <h3>Review Summary</h3>
                <div className="rating-breakdown">
                  {[5, 4, 3, 2, 1].map(rating => {
                    const count = reviewStats.ratingDistribution[rating] || 0;
                    const percentage = reviewStats.totalReviews > 0 ? 
                      (count / reviewStats.totalReviews * 100).toFixed(1) : 0;
                    return (
                      <div key={rating} className="rating-bar">
                        <span>{rating} ⭐</span>
                        <div className="bar-container">
                          <div 
                            className="bar-fill" 
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                        <span>{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div className="reviews-content">
            <div className="reviews-header">
              <h3>User Reviews</h3>
              {currentUser && (
                <button 
                  onClick={() => setShowReviewForm(true)} 
                  className="write-review-btn"
                >
                  Write Review
                </button>
              )}
            </div>

            {reviews.length > 0 ? (
              <div className="reviews-list">
                {reviews.map(review => (
                  <div key={review.id} className="review-item">
                    <div className="review-header">
                      <div className="reviewer-info">
                        <span className="reviewer-name">
                          {review.user?.display_name || 'Anonymous'}
                        </span>
                        <span className="review-date">
                          {new Date(review.review_date).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="review-rating">
                        {renderRatingStars(review.rating)}
                      </div>
                    </div>
                    <div className="review-text">
                      {review.review_text}
                    </div>
                    <div className="review-footer">
                      <button 
                        onClick={() => handleReviewHelpful(review.id)}
                        className="helpful-btn"
                      >
                        👍 Helpful ({review.helpful_count || 0})
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-reviews">
                <p>No reviews yet. Be the first to review this app!</p>
              </div>
            )}
          </div>
        )}

        {/* Details Tab */}
        {activeTab === 'details' && (
          <div className="details-content">
            <div className="app-details-grid">
              <div className="detail-item">
                <h4>Developer</h4>
                <p>{app.app_developer?.developer_name || 'Unknown'}</p>
              </div>
              <div className="detail-item">
                <h4>Category</h4>
                <p>{app.app_category}</p>
              </div>
              <div className="detail-item">
                <h4>Version</h4>
                <p>{app.app_version || '1.0'}</p>
              </div>
              <div className="detail-item">
                <h4>Last Updated</h4>
                <p>{new Date(app.updated_at).toLocaleDateString()}</p>
              </div>
              <div className="detail-item">
                <h4>Size</h4>
                <p>{app.app_size || 'Unknown'}</p>
              </div>
              <div className="detail-item">
                <h4>Price</h4>
                <p>{app.is_free ? 'Free' : `$${app.subscription_price || 0}`}</p>
              </div>
            </div>

            {app.grade_levels && app.grade_levels.length > 0 && (
              <div className="grade-levels">
                <h4>Grade Levels</h4>
                <div className="grade-tags">
                  {app.grade_levels.map((grade, index) => (
                    <span key={index} className="grade-tag">{grade}</span>
                  ))}
                </div>
              </div>
            )}

            {app.requirements && (
              <div className="requirements">
                <h4>Requirements</h4>
                <p>{app.requirements}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Review Form Modal */}
      {showReviewForm && renderReviewForm()}
    </div>
  );
};

export default AppDetails;
